<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getKfAccounts, deleteKfAccount } from '@/api/weworkKf'
import ListPageShell from '@/components/ListPageShell.vue'
import KfAccountFormModal from './components/KfAccountFormModal.vue'
import KfServicerModal from './components/KfServicerModal.vue'

// 数据
const loading = ref(false)
const accountList = ref([])

// 表单弹窗状态
const showFormModal = ref(false)
const isEdit = ref(false)
const editAccountData = ref(null)
const formModalRef = ref()

// 接待人员弹窗状态
const showServicerModal = ref(false)
const currentAccount = ref(null)

// 状态配置
const statusConfig = {
  1: { label: '启用', dot: 'bg-emerald-400', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  0: { label: '禁用', dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-100' }
}

// 获取数据
async function fetchData() {
  loading.value = true
  try {
    const data = await getKfAccounts()
    accountList.value = data || []
  } catch (error) {
    ElMessage.error('加载客服账号列表失败')
  } finally {
    loading.value = false
  }
}

// 新增
const handleAdd = () => {
  isEdit.value = false
  editAccountData.value = null
  showFormModal.value = true
}

// 编辑
const handleEdit = (item) => {
  isEdit.value = true
  editAccountData.value = { ...item }
  showFormModal.value = true
}

// 表单提交成功回调
const handleFormSuccess = () => {
  formModalRef.value?.done()
  fetchData()
}

// 表单提交失败回调
const handleFormError = () => {
  formModalRef.value?.done()
}

// 删除
const handleDelete = async (item) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除客服账号「${item.name}」吗？删除后无法恢复。`,
      '确认删除',
      {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    await deleteKfAccount(item.id)
    ElMessage.success('删除成功')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('删除失败')
    }
  }
}

// 管理接待人员
const handleManageServicers = (item) => {
  currentAccount.value = item
  showServicerModal.value = true
}

// 格式化时间
const formatTime = (time) => {
  if (!time) return '-'
  return new Date(time).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

onMounted(() => {
  fetchData()
})
</script>

<template>
  <ListPageShell>
    <template #header>
    <!-- 页面头部 -->
    <div class="list-page-panel is-header-minimal">
      <div class="list-page-heading">
      <div class="list-page-heading-main">
        <h1 class="text-xl font-semibold text-slate-900">微信客服管理</h1>
        <p class="text-sm text-slate-500 mt-1">管理企业微信客服账号及接待人员</p>
      </div>
      <div class="list-page-heading-actions">
        <button
          @click="handleAdd"
          class="btn-primary inline-flex items-center gap-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          新增客服账号
        </button>
      </div>
    </div>
    </div>
    </template>

    <!-- 加载骨架屏 -->
    <template v-if="loading">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div v-for="n in 6" :key="n" class="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
          <div class="flex items-center gap-4 mb-4">
            <div class="w-12 h-12 bg-slate-200 rounded-full shrink-0"></div>
            <div class="flex-1">
              <div class="h-4 bg-slate-200 rounded w-24 mb-2"></div>
              <div class="h-3 bg-slate-200 rounded w-40"></div>
            </div>
          </div>
          <div class="h-3 bg-slate-200 rounded w-32 mb-3"></div>
          <div class="h-8 bg-slate-200 rounded w-full"></div>
        </div>
      </div>
    </template>

    <!-- 空状态 -->
    <div v-else-if="accountList.length === 0" class="bg-white rounded-xl border border-slate-200 py-20">
      <div class="flex flex-col items-center">
        <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-3">
          <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
          </svg>
        </div>
        <p class="text-slate-500 text-sm mb-1">暂无客服账号</p>
        <p class="text-slate-400 text-xs">点击上方按钮创建第一个客服账号</p>
      </div>
    </div>

    <!-- 客服账号卡片列表 -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="item in accountList"
        :key="item.id"
        class="bg-white rounded-xl border border-slate-200 hover:shadow-md transition-shadow"
      >
        <!-- 卡片头部 -->
        <div class="p-5">
          <div class="flex items-start gap-4">
            <!-- 头像 -->
            <div class="shrink-0">
              <img
                v-if="item.avatar"
                :src="item.avatar"
                :alt="item.name"
                class="w-12 h-12 rounded-full object-cover border-2 border-slate-100"
              />
              <div v-else class="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                </svg>
              </div>
            </div>

            <!-- 名称与状态 -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <h3 class="text-sm font-semibold text-slate-900 truncate">{{ item.name }}</h3>
                <span
                  :class="[statusConfig[item.status]?.bg, statusConfig[item.status]?.text]"
                  class="status-badge shrink-0"
                >
                  <span :class="statusConfig[item.status]?.dot" class="w-1.5 h-1.5 rounded-full"></span>
                  {{ statusConfig[item.status]?.label || '未知' }}
                </span>
              </div>
              <p class="text-xs text-slate-400 truncate" :title="item.openKfid">
                ID: {{ item.openKfid || '-' }}
              </p>
            </div>

            <!-- 操作菜单 -->
            <div class="flex items-center gap-0.5 shrink-0">
              <button
                @click="handleEdit(item)"
                class="table-action-button is-primary"
                title="编辑"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
              </button>
              <button
                @click="handleDelete(item)"
                class="table-action-button is-danger"
                title="删除"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- 创建时间 -->
          <div class="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            {{ formatTime(item.createTime) }}
          </div>
        </div>

        <!-- 卡片底部操作 -->
        <div class="px-5 py-3 border-t border-slate-100 bg-slate-50/50 rounded-b-xl">
          <button
            @click="handleManageServicers(item)"
            class="btn-outline-primary w-full inline-flex items-center justify-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
            管理接待人员
          </button>
        </div>
      </div>
    </div>

    <!-- 新增/编辑客服账号弹窗 -->
    <KfAccountFormModal
      ref="formModalRef"
      v-model:show="showFormModal"
      :is-edit="isEdit"
      :account-data="editAccountData"
      @success="handleFormSuccess"
      @error="handleFormError"
    />

    <!-- 接待人员管理弹窗 -->
    <KfServicerModal
      v-model:show="showServicerModal"
      :account="currentAccount"
    />
  </ListPageShell>
</template>
